/**
 * BarredUserMessage - Component to display barred user message
 * 
 * Shows appropriate message when user is barred and provides contact information
 */

import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { deobfuscateEmail } from '../utils/emailObfuscation';

// Store email in obfuscated format to prevent email harvesting (CWE-200)
// Format: @ -> [at], . -> [dot]
const OBFUSCATED_CONTACT_EMAIL = 'admin[at]vigilance[dot]gov[dot]in';

const BarredUserMessage = () => {
  const { user, getUserRole } = useAuth();

  const userRole = getUserRole();
  const userName = user?.name || user?.fullname || user?.email || 'User';

  const getContactInfo = () => {
    switch (userRole) {
      case 'cvo':
        return {
          title: 'CVO Account Suspended',
          message: 'Your CVO account has been suspended. Please contact the system administrator.',
          contact: 'Contact CVO Administration'
        };
      case 'authority':
        return {
          title: 'Authority Account Suspended',
          message: 'Your authority account has been suspended. Please contact the system administrator.',
          contact: 'Contact Authority Administration'
        };
      case 'citizen':
        return {
          title: 'Citizen Account Suspended',
          message: 'Your citizen account has been suspended. Please contact the vigilance office.',
          contact: 'Contact Vigilance Office'
        };
      default:
        return {
          title: 'Account Suspended',
          message: 'Your account has been suspended. Please contact the system administrator.',
          contact: 'Contact Administrator'
        };
    }
  };

  const contactInfo = getContactInfo();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {/* Warning Icon */}
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
            <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          
          {/* Title */}
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {contactInfo.title}
          </h2>
          
          {/* Message */}
          <p className="mt-2 text-sm text-gray-600">
            Hello {userName},
          </p>
          <p className="mt-2 text-sm text-gray-600">
            {contactInfo.message}
          </p>
          
          {/* Contact Information */}
          <div className="mt-6 p-4 bg-gray-100 rounded-lg">
            <h3 className="text-sm font-medium text-gray-900 mb-2">
              {contactInfo.contact}
            </h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p>Email: {OBFUSCATED_CONTACT_EMAIL}</p>
              <p>Phone: +91-XXX-XXX-XXXX</p>
              <p>Office Hours: 9:00 AM - 5:00 PM (Mon-Fri)</p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="mt-6 space-y-3">
            <button
              onClick={() => window.location.href = '/'}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Return to Home
            </button>
            
            <button
              onClick={() => {
                // Deobfuscate email for mailto link (only at runtime)
                const email = deobfuscateEmail(OBFUSCATED_CONTACT_EMAIL);
                window.location.href = `mailto:${email}?subject=Account Suspension Inquiry`;
              }}
              className="group relative w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarredUserMessage;
